import { type PillColor } from '@/shared/components/flip-ui-kit/Pill';
import { OFFSET_BPS, useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { percentToPips, pipsToPercent } from '@/shared/utils/numbers';

export const LoanRiskTypes = ['Conservative', 'Optimal', 'Risky', 'Liquidation'] as const;
export type LoanRiskType = (typeof LoanRiskTypes)[number];

export interface LoanRiskConfig {
  color: PillColor;
  range: [number, number];
}

export const getLoanRiskTypeConfig = (
  optimalThresholdBps: number,
  liquidationThresholdBps: number,
): Record<LoanRiskType, LoanRiskConfig> => ({
  Conservative: {
    color: 'green',
    range: [0, optimalThresholdBps / 2],
  },
  Optimal: {
    color: 'neon-yellow',
    range: [optimalThresholdBps / 2, optimalThresholdBps - 1000 + OFFSET_BPS],
  },
  Risky: {
    color: 'yellow',
    range: [optimalThresholdBps - 1000 + OFFSET_BPS, liquidationThresholdBps],
  },
  Liquidation: {
    color: 'red',
    range: [liquidationThresholdBps, percentToPips(100)],
  },
});

const getLoanRiskTypeByBps = (
  bps: number,
  config: Record<LoanRiskType, LoanRiskConfig>,
): LoanRiskType => {
  for (const [type, { range }] of Object.entries(config) as [LoanRiskType, LoanRiskConfig][]) {
    const [min, max] = range;
    if (bps >= min && bps < max) {
      return type;
    }
  }
  return 'Liquidation';
};

export const getPositionByLabel = (
  label: LoanRiskType,
  optimalThresholdBps = 8_000, // 80%
  riskyThresholdBps = 9_000, // 90%
): number => {
  const config = getLoanRiskTypeConfig(optimalThresholdBps, riskyThresholdBps);
  const { range } = config[label];
  if (label === 'Risky') return pipsToPercent(range[0] + percentToPips(5));
  if (label === 'Liquidation') return pipsToPercent(range[1]);
  return pipsToPercent(range[0]);
};

const getLoanRiskGradient = (optimalThresholdBps: number, riskyThresholdBps: number) => `
  linear-gradient(
    to right,
    #46DA93 0%,
    #F3D55B ${pipsToPercent(optimalThresholdBps) / 2}%,
    #F7931A ${pipsToPercent(optimalThresholdBps)}%,
    #F7931A ${pipsToPercent(optimalThresholdBps)}%,
    #F71A1A ${pipsToPercent(riskyThresholdBps)}%,
    #CF2626 100%
  )
`;

export const useLoanRiskType = (ltvBps: number) => {
  const { targetLtvThresholdBps, softLiquidationLtvThresholdBps } = useLendingConfig();

  const optimal = targetLtvThresholdBps;
  const risky =
    targetLtvThresholdBps + (softLiquidationLtvThresholdBps - targetLtvThresholdBps) / 2;
  const liquidation = softLiquidationLtvThresholdBps;

  const config = getLoanRiskTypeConfig(optimal, liquidation);
  const riskType = getLoanRiskTypeByBps(ltvBps, config);
  const gradient = getLoanRiskGradient(optimal, risky);
  const riskColor = config[riskType].color;

  return {
    riskType,
    riskColor,
    config,
    gradient,
    isLtvHighRisk: riskType !== 'Conservative' && riskType !== 'Optimal',
  };
};
